// External libraries
import { Component } from "react";

// Preact Signals
import { effect } from "@preact/signals-react";

// Kendo React components
import { Calendar, CalendarCell, DateTimePicker } from "@progress/kendo-react-dateinputs";
import { FieldWrapper } from "@progress/kendo-react-form";
import { Error, Hint, Label } from "@progress/kendo-react-labels";
import { Ripple } from "@progress/kendo-react-ripple";

// Internal components
import { If } from "../../Common/UI/IfComponent";
import { TooltipPopupIcon } from "../../Common/UI/TooltipPopupIcon";

// Utilities
import { validators } from "../../Form/validators";
import math from "../../Mathjs/CustomMathjsInstance";
import { Invisible, validateStageStep } from "../../Utilities/EntryUtils";

// Signals
import {
  clearEntryFields,
  disabledSections,
  entryId,
  entryValues,
  formErrors,
  readOnlyEntry,
  template,
} from "../../Entry/Manage/EditEntrySignals";

const customCell = (props) => {
  const isWeekend = props.isWeekend;
  const style = props.isWeekend
    ? {
        color: "red",
      }
    : {
        fontWeight: "normal",
      };

  return (
    <CalendarCell
      {...props}
      style={style}
      isDisabled={isWeekend}
    />
  );
};

const CustomCalendar = (props) => {
  return (
    <Calendar
      {...props}
      cell={customCell}
      className="customCalendar"
    />
  );
};

export class DateTimeField extends Component {
  constructor({
    field,
    handleFieldChange,
    sectionIndex,
    fieldIndex,
    labelVisible,
    eventIndex,
    value,
    galleryParentName,
    isStageField,
    isPrintPreview,
  }) {
    super();
    const { fieldName, properties, revisionCount } = field;
    this.state = {
      value: new Date(value),
      valid: this.validate(value, properties?.IsRequired),
      invisible: false,
      disabled: false,
      revisionCount: field.properties?.RevisionCount ? revisionCount ?? 0 : null,
      touched: false,
    };
    this.fieldName = typeof eventIndex === "number" ? `${fieldName}_${eventIndex}` : fieldName;
    this.weekDaysOnly = properties?.WeekDaysOnly;
    this.hoursOnly = properties?.HoursOnly;
    this.isRequired = properties?.IsRequired;
    this.isReadOnly = properties?.IsReadOnly;
    this.displayNameHorizontally = properties?.DisplayNameHorizontally;
    this.hint = properties?.Hint;
    this.fieldInformation = properties?.FieldInformation;
    this.defaultValueExpression = properties?.Expression;
    this.visibilityRules = properties?.VisibilityRules;
    this.parameterValues = properties?.ParameterValues;
    this.propRevCount = properties?.RevisionCount;
    this.labelVisible = labelVisible;
    this.label = properties?.Label || `${fieldName}:`;
    this.sectionIndex = sectionIndex;
    this.fieldIndex = fieldIndex;
    this.eventIndex = eventIndex;
    this.handleFieldChange = handleFieldChange;
    this.galleryParentName = galleryParentName;
    this.isStageField = isStageField;
    this.isPrintPreview = isPrintPreview;
    this.initialRevisionCount = properties?.RevisionCount ? revisionCount ?? 0 : null;
    this.initialValue = new Date(value);
    this.sectionName = template.value?.sections?.[sectionIndex]?.sectionName;
    this.validationMessage = "Error: A date and time is required.";
    this.disposeValidationEffect = () => {};
    this.disposeDependencyEffect = () => {};
    this.disposeClearFieldsEffect = () => {};
    this.disposeDisabledSectionsEffect = () => {};
  }

  //#region lifecycle functions
  componentDidMount() {
    this.handleClearFieldEffect();
    if (this.isRequired) {
      if (!this.validate(this.state.value, this.isRequired)) {
        const newFormErrors = { ...formErrors.value };
        newFormErrors[this.sectionName] = newFormErrors[this.sectionName] || [];
        newFormErrors[this.sectionName].push(this.fieldName);
        formErrors.value = newFormErrors;
      }
      this.handleValidationEffect();
      this.updateFormErrors();
    }
    if (this.visibilityRules) this.handleDependencyEffect();
    if (this.defaultValueExpression && isNaN(this.state.value))
      this.calculateDefaultValueExpression();

    this.disposeDisabledSectionsEffect = effect(() => {
      const isSectionDisabled = disabledSections.value[this.sectionName];
      if (this.state.disabled !== isSectionDisabled) {
        this.setState({ disabled: isSectionDisabled });
      }
    });
  }

  componentWillUnmount() {
    this.disposeValidationEffect();
    this.disposeDependencyEffect();
    this.disposeClearFieldsEffect();
    this.disposeDisabledSectionsEffect();
  }

  shouldComponentUpdate(_, nextState) {
    return (
      this.state.value !== nextState.value ||
      this.state.valid !== nextState.valid ||
      this.state.invisible !== nextState.invisible ||
      this.state.disabled !== nextState.disabled ||
      this.state.touched !== nextState.touched
    );
  }

  getSnapshotBeforeUpdate(_, prevState) {
    return {
      visibilityChange: prevState.invisible && !this.state.invisible,
    };
  }

  componentDidUpdate(_, __, snapshot) {
    if (snapshot.visibilityChange) this.setDefaultValue();
    if (this.state.invisible) this.clearField();
    if (!this.state.invisible && this.isRequired) {
      this.setState(
        (prevState) => ({ valid: this.validate(prevState.value, this.isRequired) }),
        () => {
          this.updateFormErrors();
        }
      );
    }
    if (this.state.disabled) this.clearFormErrors();
  }
  //#endregion lifecycle functions

  //#region custom functions
  validate(value, isRequired) {
    if (isRequired) {
      return validators.datetime(value);
    }
    if (this.weekDaysOnly) {
      return validators.dateWeekDaysOnly(value);
    }
    return true;
  }

  handleClearFieldEffect() {
    this.disposeClearFieldsEffect = effect(() => {
      if (clearEntryFields.value) {
        if (this.defaultValueExpression) {
          this.calculateDefaultValueExpression();
        } else if (entryValues.value[this.fieldName] !== "") {
          this.setState({ value: null });
          this.handleFieldChange({
            e: { target: { name: this.fieldName }, value: "" },
            sectionIndex: this.sectionIndex,
            fieldIndex: this.fieldIndex,
            eventIndex: this.eventIndex,
          });
        }
      }
    });
  }

  handleValidationEffect() {
    this.disposeValidationEffect = effect(() => {
      this.setState({
        valid: formErrors.value[this.sectionName]
          ? formErrors.value[this.sectionName]?.indexOf(this.fieldName) === -1
          : true,
      });
    });
  }

  updateFormErrors() {
    if (!this.state.valid) {
      if (
        formErrors.value[this.sectionName]?.indexOf(this.fieldName) === -1 ||
        !formErrors.value[this.sectionName]
      ) {
        const newFormErrors = { ...formErrors.value };
        newFormErrors[this.sectionName] = newFormErrors[this.sectionName] || [];
        newFormErrors[this.sectionName].push(this.fieldName);
        formErrors.value = newFormErrors;
      }
    } else {
      this.clearFormErrors();
    }
  }

  clearFormErrors() {
    const newFormErrors = { ...formErrors.value };
    const sectionErrors = newFormErrors[this.sectionName];
    if (sectionErrors) {
      newFormErrors[this.sectionName] = sectionErrors.filter((error) => error !== this.fieldName);
    }
    formErrors.value = newFormErrors;
  }

  handleDependencyEffect() {
    this.disposeDependencyEffect = effect(() => {
      if (this.defaultValueExpression && isNaN(this.state.value))
        this.calculateDefaultValueExpression();
      const result = Invisible({
        VisibilityRules: this.visibilityRules,
        parameterValues: this.parameterValues,
        eventIndex: this.eventIndex,
        galleryName: this.galleryParentName,
      });

      this.setState({
        invisible: result.invisible && !result.disabled,
        disabled: result.invisible && result.disabled,
      });
    });
  }

  clearField() {
    this.clearFormErrors();
    this.setState({ value: "" });
    this.handleFieldChange({
      e: { target: { name: this.fieldName }, value: "" },
      sectionIndex: this.sectionIndex,
      fieldIndex: this.fieldIndex,
      eventIndex: this.eventIndex,
    });
  }

  setDefaultValue() {
    if (this.defaultValueExpression) this.calculateDefaultValueExpression();
  }

  onChange(e) {
    if (!this.isPrintPreview) {
      if (e.value === null || e.value === "") {
        this.setState(
          {
            valid: this.validate(e.value, this.isRequired),
            value: e.value,
            touched: true,
          },
          () => {
            this.updateFormErrors();
            this.handleFieldChange({
              e: { ...e, value: "" },
              sectionIndex: this.sectionIndex,
              fieldIndex: this.fieldIndex,
              eventIndex: this.eventIndex,
            });
            if (this.isStageField) validateStageStep();
          }
        );
        return;
      }
      let newValue = e.value;
      if (this.hoursOnly) {
        newValue.setMinutes(0);
        newValue.setSeconds(0);
        newValue.setMilliseconds(0);
      }

      const timeZoneOffset = newValue.getTimezoneOffset() * 60000;
      const adjustedDate = new Date(newValue.getTime() - timeZoneOffset)
        .toISOString()
        .replace("Z", "");
      this.setState(
        {
          touched: true,
          valid: this.validate(adjustedDate, this.isRequired),
          value: new Date(adjustedDate),
        },
        () => {
          this.updateFormErrors();
          this.incrementRevisionCount({ newValue: adjustedDate });
          this.handleFieldChange({
            e: { ...e, value: adjustedDate },
            sectionIndex: this.sectionIndex,
            fieldIndex: this.fieldIndex,
            eventIndex: this.eventIndex,
          });
          if (this.isStageField) validateStageStep();
        }
      );
    }
  }

  onBlur() {
    this.setState((prevState) => ({
      valid: this.validate(prevState.value, this.isRequired),
      touched: true,
    }));
    this.updateFormErrors();
    if (this.isStageField) validateStageStep();
  }

  incrementRevisionCount({ newValue }) {
    if (this.propRevCount && entryValues.value[this.fieldName] !== "" && entryId.value) {
      const newDate = new Date(newValue);
      const newRevisionCount =
        newDate > this.initialValue
          ? (parseInt(this.initialRevisionCount, 10) + 1).toString()
          : this.initialRevisionCount;
      this.setState({ revisionCount: newRevisionCount }, () => {
        const newTemplate = JSON.tryParse(JSON.stringify(template.value));
        newTemplate.sections[this.sectionIndex].fields[this.fieldIndex].revisionCount =
          newRevisionCount;
        template.value = newTemplate;
      });
    }
  }

  getClassName() {
    if (this.isRequired) {
      if (this.state.valid) {
        return "!k-valid";
      } else {
        return "!k-invalid";
      }
    }
  }

  calculateDefaultValueExpression() {
    try {
      let sanitizedExpression = this.defaultValueExpression;
      const values = entryValues.value;
      const parser = math.parser();

      parser.set("split", (str, separator) => str.split(separator));

      if (typeof this.eventIndex === "number" && this.galleryParentName) {
        const galleryValues = values[this.galleryParentName];
        if (!galleryValues?.[this.eventIndex]) {
          console.log("Gallery values or event index is undefined. Skipping evaluation.");
          return;
        }
        const galleryDataRow = galleryValues[this.eventIndex];
        Object.entries(galleryDataRow).forEach(([subKey, subValue]) => {
          const sanitizedSubKey = "_" + subKey.replace(/\s/g, "");
          const regex = new RegExp(`\\b${subKey.replace(/\s/g, "")}\\b`, "g");
          sanitizedExpression = sanitizedExpression.replace(regex, sanitizedSubKey);
          parser.set(sanitizedSubKey, subValue);
        });
      } else if (typeof eventIndex !== "number") {
        Object.entries(values).forEach(([key, value]) => {
          const sanitizedKey = "_" + key.replace(/\s/g, "");
          const regex = new RegExp(`\\b${key.replace(/\s/g, "")}\\b`, "g");
          sanitizedExpression = sanitizedExpression.replace(regex, sanitizedKey);
          parser.set(sanitizedKey, value);
        });
      } else {
        return;
      }

      if (this.parameterValues) {
        Object.entries(this.parameterValues).forEach(([key, value]) => {
          const sanitizedKey = "_" + key.replace(/\s/g, "");
          const regex = new RegExp(`\\b${key.replace(/\s/g, "")}\\b`, "g");
          sanitizedExpression = sanitizedExpression.replace(regex, sanitizedKey);
          parser.set(sanitizedKey, value);
        });
      }
      let result = parser.evaluate(sanitizedExpression) ?? "";
      if (JSON.stringify(this.state.value) !== JSON.stringify(result)) {
        this.setState({ value: result }, () => {
          this.onChange({
            syntheticEvent: { type: "change" },
            nativeEvent: {},
            value: this.state.value,
            show: false,
            target: {
              name: this.fieldName,
              props: {},
            },
          });
        });
      }
    } catch (error) {
      console.log(error);
      if (this.state.value) {
        this.setState({ value: "" }, () => {
          this.onChange({
            syntheticEvent: { type: "change" },
            nativeEvent: {},
            value: this.state.value,
            show: false,
            target: {
              name: this.fieldName,
              props: {},
            },
          });
        });
      }
    }
  }
  //#endregion custom functions

  render() {
    if (this.state.invisible) return null;
    return (
      <Ripple>
        <div className="k-d-flex k-flex-col">
          <FieldWrapper>
            <div
              className={this.displayNameHorizontally ? "k-d-flex k-align-items-center k-mt-5" : ""}
            >
              {/* Label to the left */}
              {this.displayNameHorizontally && this.labelVisible && (
                <Label
                  editorId={this.fieldName}
                  editorValid={this.isRequired ? this.state.valid : true}
                  style={{
                    marginRight: "8px",
                    fontWeight: "bold",
                  }}
                >
                  {this.label}
                </Label>
              )}

              {/* DateTimePicker Container*/}
              <div
                className="k-form-field-wrap"
                style={{ flex: 1 }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                  <DateTimePicker
                    data-testid={this.fieldName}
                    className={this.getClassName()}
                    id={this.fieldName}
                    name={this.fieldName}
                    label={!this.displayNameHorizontally && this.labelVisible ? this.label : null}
                    calendar={this.weekDaysOnly ? CustomCalendar : null}
                    onChange={(e) => this.onChange(e)}
                    onBlur={() => this.onBlur()}
                    value={isNaN(this.state.value) ? null : this.state.value}
                    valid={this.isRequired ? this.state.valid : true}
                    disabled={this.isReadOnly || this.state.disabled || readOnlyEntry.value}
                  />
                  <If
                    condition={
                      this.fieldInformation !== undefined &&
                      this.fieldInformation !== "" &&
                      this.fieldInformation !== "<p></p>"
                    }
                  >
                    <TooltipPopupIcon content={this.fieldInformation} />
                  </If>
                </div>
              </div>
            </div>
            <If condition={this.hint}>
              <Hint>{this.hint}</Hint>
            </If>
            <If condition={!this.state.valid && this.state.touched}>
              <Error>{this.validationMessage}</Error>
            </If>
          </FieldWrapper>
        </div>
      </Ripple>
    );
  }
}
